import React from 'react';
import { LuMapPin, LuZap, LuNavigation, LuChevronRight } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge/Badge';
import StationPhoto from '../../../components/StationPhoto/StationPhoto.jsx';
import { resolveStationPhotoUrl } from '../../../utils/stationPhotos.js';
import { AVAILABILITY } from '../utils/stationAvailability.js';
import { formatKm } from '../utils/distance.js';
import { directionsUrl } from '../utils/directions.js';
import { isNative } from '../../../platform/platform.js';
import { openInMaps } from '../../../platform/navigation.js';
import './ChargerCard.less';

function actionLabelFor(availability) {
  return availability === AVAILABILITY.AVAILABLE ? 'View charger' : 'View details';
}

/**
 * ChargerCard — one row in the Find-a-charger list.
 *
 * Layout has three zones:
 *   1. Left — a small map-pin icon block so every row starts with a
 *      consistent visual anchor.
 *   2. Content — name + code pill on line 1, address on line 2,
 *      power/connectors/type on line 3, status badge on line 4.
 *   3. Right — a stacked action column with "View charger" (primary
 *      brand button) and "Get directions" (secondary outline
 *      button), independent from each other and from the row's
 *      background click target.
 *
 * The whole row is clickable via a full-cover hit-area overlay
 * (`.gc-charger-row-hitarea`) that lives beneath the two right-side
 * actions in stacking order. That way:
 *   - Clicking the card body or the "View charger" button opens the
 *     charger profile.
 *   - Clicking the "Get directions" anchor opens Google Maps in a
 *     new tab; because the anchor sits on top of the hit-area, the
 *     underlying click never fires, and it also calls
 *     stopPropagation + preventDefault as belt-and-braces so a
 *     future stacking change can't reintroduce the bug.
 *
 * Directions are generated ONLY from THIS row's own normalised
 * coordinates — no map centre, no user location, no marker state,
 * no fallback. Rows without valid coords render no directions
 * action at all (never a disabled button).
 */
export default function ChargerCard({ station, onSelect, highlighted = false }) {
  const {
    displayName,
    chargerCode,
    locationText,
    distanceKm,
    availability,
    statusLabel,
    statusTone,
    powerKw,
    availableConnectors,
    totalConnectors,
    connectorType,
  } = station;

  const distanceStr = Number.isFinite(distanceKm) ? formatKm(distanceKm) : null;
  const powerStr = Number.isFinite(powerKw) ? `${powerKw} kW` : null;
  // Physical-connector display. The API now excludes OCPP
  // connector_id = 0 from `totalConnectors`, so a single-port
  // charger arrives as `{ total: 1, available: 0|1 }` (previously
  // showed as `2` due to the connector-0 count leak). Use the
  // singular "connector" for exactly one physical port.
  const connectorsStr = Number.isFinite(totalConnectors) && totalConnectors > 0
    ? `${availableConnectors ?? 0}/${totalConnectors} ${totalConnectors === 1 ? 'connector' : 'connectors'}`
    : null;
  const specLine = [powerStr, connectorsStr, connectorType].filter(Boolean).join(' · ');
  const directions = directionsUrl({ lat: station.lat, lng: station.lng });

  const title = displayName || chargerCode || 'Charger';

  const handleHitAreaKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect?.();
    }
  };

  const handleDirectionsClick = (e) => {
    // Belt-and-braces: the anchor sits above the hit-area overlay in
    // z-index, so the underlying click already doesn't fire — but a
    // future stacking or overlap change shouldn't be able to
    // reintroduce the "get directions ALSO opens the profile" bug.
    e.stopPropagation();
    // On native we prefer the platform-appropriate maps app (Apple
    // Maps on iOS, `geo:` intent on Android) via the platform layer.
    // The web path stays untouched — the anchor's default behaviour
    // still opens Google Maps in a new tab, and right-click "copy
    // link" continues to work because the `href` is preserved.
    if (isNative() && Number.isFinite(station.lat) && Number.isFinite(station.lng)) {
      e.preventDefault();
      openInMaps({ lat: station.lat, lng: station.lng, label: title }).catch(() => {});
    }
  };

  return (
    <div
      className={`gc-charger-row${highlighted ? ' is-highlighted' : ''}`}
      data-station-key={station.stationId || station.chargerCode || undefined}
      aria-label={`${title}${statusLabel ? ` — ${statusLabel}` : ''}`}
    >
      {/* Full-cover hit-area — makes the whole card clickable for
          View charger. It's a real <button> so keyboard users can
          activate the row with Enter/Space and screen readers see a
          single actionable region. */}
      <button
        type="button"
        className="gc-charger-row-hitarea"
        onClick={onSelect}
        onKeyDown={handleHitAreaKey}
        aria-label={`${actionLabelFor(availability)}: ${title}`}
      />

      {/* Zone 1 — station photo thumbnail. Replaces the previous
          plain map-pin icon block. StationPhoto renders the icon
          fallback in the same visual slot when no photo exists, so
          rows without a photo still get a consistent visual anchor.
          Aspect ratio and skeleton are handled by the component so
          the row height is stable whether the image has loaded or
          not — no layout shift. */}
      <StationPhoto
        photoUrl={resolveStationPhotoUrl(station)}
        variant="thumbnail"
        alt=""
        className="gc-charger-row-photo"
      />

      {/* Zone 2 — content */}
      <div className="gc-charger-row-body">
        {highlighted && (
          <p className="gc-charger-row-nearest">Nearest to you</p>
        )}
        <div className="gc-charger-row-heading">
          <h3 className="gc-charger-row-title">{title}</h3>
          {chargerCode && (
            <span className="gc-charger-row-code">{chargerCode}</span>
          )}
        </div>

        {(locationText || distanceStr) && (
          <p className="gc-charger-row-address">
            <LuMapPin aria-hidden="true" />
            <span>
              {locationText || 'Location not set'}
              {distanceStr ? ` · ${distanceStr}` : ''}
            </span>
          </p>
        )}

        {specLine && (
          <p className="gc-charger-row-specs">
            <LuZap aria-hidden="true" />
            <span>{specLine}</span>
          </p>
        )}

        {statusLabel && (
          <div className="gc-charger-row-status">
            <Badge tone={statusTone}>{statusLabel}</Badge>
          </div>
        )}
      </div>

      {/* Zone 3 — action stack. Both actions are real buttons /
          anchors that sit above the hit-area in stacking order. */}
      <div className="gc-charger-row-actions">
        <button
          type="button"
          className="gc-charger-row-primary"
          onClick={(e) => {
            // Prevent the underlying hit-area handler from also
            // firing (belt-and-braces: the buttons already sit
            // above the overlay).
            e.stopPropagation();
            onSelect?.();
          }}
        >
          <span>{actionLabelFor(availability)}</span>
          <LuChevronRight aria-hidden="true" />
        </button>

        {directions && (
          <a
            className="gc-charger-row-secondary"
            href={directions}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleDirectionsClick}
            aria-label={`Get directions to ${title}`}
          >
            <LuNavigation aria-hidden="true" />
            <span>Get directions</span>
          </a>
        )}
      </div>
    </div>
  );
}
